/**
 * Toast notification component — Brand Book §2.16
 * bg-card, border, shadow-md, 3px left accent colored by type
 * Feather icon (colored), neutral text, close X
 * Max 3 visible, bottom-right desktop, bottom-center mobile
 */

import { clsx } from 'clsx';
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'react-feather';
import { useUIStore, type ToastType } from '../../stores/uiStore';

const icons: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle size={16} className="text-success" />,
  error: <XCircle size={16} className="text-error" />,
  warning: <AlertTriangle size={16} className="text-warning" />,
  info: <Info size={16} className="text-info" />,
};

const accentColors: Record<ToastType, string> = {
  success: 'border-l-success',
  error: 'border-l-error',
  warning: 'border-l-warning',
  info: 'border-l-info',
};

export function Toast() {
  const { toasts, dismissToast } = useUIStore();

  if (toasts.length === 0) {return null;}

  // Brand book: max 3 visible, newest on top
  const visibleToasts = toasts.slice(-3);

  return (
    <div
      className={clsx(
        'fixed z-50 flex flex-col gap-3',
        'bottom-4 right-4',
        'max-md:right-auto max-md:left-1/2 max-md:-translate-x-1/2'
      )}
    >
      {visibleToasts.map((toast) => (
        <div
          key={toast.id}
          className={clsx(
            'flex items-center gap-3 px-3 py-3',
            'bg-surface-card border border-border shadow-md',
            'border-l-[3px]',
            accentColors[toast.type],
            'max-w-[320px] min-w-[280px]'
          )}
          style={{ animation: 'toastEnter 200ms ease' }}
          role="status"
          aria-live="polite"
        >
          <span className="flex-shrink-0">{icons[toast.type]}</span>
          <p className="text-body text-txt-primary flex-1">{toast.message}</p>
          {toast.action && (
            <button
              onClick={() => {
                toast.action?.onClick();
                dismissToast(toast.id);
              }}
              className={clsx(
                'flex-shrink-0 px-2 py-1 text-body-sm font-medium uppercase tracking-wide',
                'text-orange hover:text-txt-primary transition-colors',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange'
              )}
            >
              {toast.action.label}
            </button>
          )}
          <button
            onClick={() => dismissToast(toast.id)}
            className={clsx(
              'flex-shrink-0 p-1 rounded-sm text-txt-tertiary',
              'hover:text-txt-primary hover:bg-surface-subtle transition-colors',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange'
            )}
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}